import axios, { AxiosError } from "axios";
import config from "../config.js";
import { Token } from "../utils/localStorage.util";
import {
  importarExtratoPdfSchemaRequest,
  uploadPDFToOpenAiRequest,
} from "../../src/@types";

/** Cliente aguarda até 3 min; gateway pode cortar antes — ver ``isImportacaoExtratoRespostaIndefinida``. */
export const IMPORTAR_EXTRATO_PDF_SCHEMA_TIMEOUT_MS = 180_000;

export const MSG_IMPORTACAO_TIMEOUT =
  "A resposta demorou mais que o esperado. Confira a lista abaixo e tente novamente se o extrato não aparecer.";

export const MSG_ERRO_PADRAO =
  "Não foi possível concluir a operação. Tente novamente em instantes.";

/** Timeout do upload multipart (arquivos grandes, ex.: PDF ~100 MB). */
export const ENFILEIRAR_IMPORTACAO_TIMEOUT_MS = 600_000;

export const MSG_ENFILEIRAR_IMPORTACAO_TIMEOUT =
  "O envio do arquivo demorou mais que o esperado. Verifique sua conexão e tente novamente.";

export const MSG_ENFILEIRAR_IMPORTACAO_REDE =
  "Não foi possível enfileirar os arquivos. Verifique sua conexão e tente novamente.";

/** Mensagem amigável a partir do corpo JSON de erro da api-openai. */
export function extrairMensagemErroImportacaoExtrato(data: unknown): string {
  if (!data || typeof data !== "object") {
    return MSG_ERRO_PADRAO;
  }
  const d = data as Record<string, unknown>;
  if (typeof d.mensagem === "string" && d.mensagem.trim()) {
    return d.mensagem.trim();
  }
  if (typeof d.message === "string" && d.message.trim()) {
    return d.message.trim();
  }
  if (typeof d.detail === "string" && d.detail.trim()) {
    return d.detail.trim();
  }
  if (Array.isArray(d.detail)) {
    const first = d.detail[0] as { msg?: string } | undefined;
    if (typeof first?.msg === "string" && first.msg.trim()) {
      return first.msg.trim();
    }
  }
  return MSG_ERRO_PADRAO;
}

/** Mensagem unificada para falhas no POST /importacoes/.../arquivos. */
export function resolverMensagemErroEnfileiramentoImportacao(
  error: unknown
): string {
  if (isImportacaoExtratoRespostaIndefinida(error)) {
    return MSG_ENFILEIRAR_IMPORTACAO_TIMEOUT;
  }
  if (axios.isAxiosError(error) && error.response?.data) {
    return extrairMensagemErroImportacaoExtrato(error.response.data);
  }
  return MSG_ENFILEIRAR_IMPORTACAO_REDE;
}

export function isImportacaoExtratoRespostaIndefinida(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  const axiosError = error as AxiosError;
  if (
    axiosError.code === "ERR_NETWORK" ||
    axiosError.code === "ECONNABORTED"
  ) {
    return true;
  }
  const status = axiosError.response?.status;
  return status === 504 || status === 502 || status === 503 || status === 408;
}

const apiIntegracaoOpenAi = axios.create({
  baseURL: config.REACT_APP_HOST_API_INTEGRACAO_OPENAI,
});

apiIntegracaoOpenAi.interceptors.request.use(
  async (config) => {
    const token = Token.getToken();
    if (token) {
      config.headers.Authorization = token;
    }

    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiIntegracaoOpenAi.interceptors.response.use(
  (response) => response.data,
  (error) => Promise.reject(error)
);

export function uploadPDFToOpenAi(body: uploadPDFToOpenAiRequest) {
  return apiIntegracaoOpenAi.post(`/upload-pdf`, body);
}

/** Importação por parser de schema (sem OpenAI); fluxo termina na api-conciliacao. */
export function importarExtratoPdfSchema(body: importarExtratoPdfSchemaRequest) {
  return apiIntegracaoOpenAi.post(`/importar-extrato-pdf-schema`, body, {
    timeout: IMPORTAR_EXTRATO_PDF_SCHEMA_TIMEOUT_MS,
  });
}

export type EnfileirarImportacaoParams = {
  idEmpresa: number | string;
  files: File[];
  codigoBanco?: string;
  contaBancaria?: string;
  origemTela?: "extrato_bancario" | "pagina_importacoes";
};

/** Enfileira PDF/OFX para processamento assíncrono (202 Accepted). */
export function enfileirarImportacaoExtrato({
  idEmpresa,
  files,
  codigoBanco,
  contaBancaria,
  origemTela = "pagina_importacoes",
}: EnfileirarImportacaoParams) {
  const form = new FormData();
  files.forEach((f) => form.append("files", f));
  if (codigoBanco) form.append("codigo_banco", codigoBanco);
  if (contaBancaria) form.append("conta_bancaria", contaBancaria);
  if (origemTela) form.append("origem_tela", origemTela);
  return apiIntegracaoOpenAi.post(`/importacoes/${idEmpresa}/arquivos`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: ENFILEIRAR_IMPORTACAO_TIMEOUT_MS,
  });
}

/** @deprecated Leitura da fila migrada para api-conciliacao — use api-conciliacao.service */
export function listarTodasImportacoesExtrato(limit = 100) {
  return apiIntegracaoOpenAi.get(`/importacoes`, { params: { limit } });
}

/** @deprecated Leitura da fila migrada para api-conciliacao */
export function listarImportacoesExtrato(idEmpresa: number | string, limit = 100) {
  return apiIntegracaoOpenAi.get(`/importacoes/${idEmpresa}`, { params: { limit } });
}

/** @deprecated Leitura da fila migrada para api-conciliacao */
export function buscarImportacaoExtrato(idEmpresa: number | string, idJob: number) {
  return apiIntegracaoOpenAi.get(`/importacoes/${idEmpresa}/${idJob}`);
}

/** @deprecated Leitura da fila migrada para api-conciliacao */
export function buscarLogsImportacaoExtrato(idEmpresa: number | string, idJob: number) {
  return apiIntegracaoOpenAi.get(`/importacoes/${idEmpresa}/${idJob}/logs`);
}

/** @deprecated Cancelamento permanece na openai até migração futura */
export function cancelarImportacaoExtrato(idEmpresa: number | string, idJob: number) {
  return apiIntegracaoOpenAi.delete(`/importacoes/${idEmpresa}/${idJob}`);
}
